import { useEffect, useState } from 'react';

interface PWAInstallPrompt extends Event {
  prompt: () => void;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export const usePWA = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<PWAInstallPrompt | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Kiểm tra xem app đã được cài đặt chưa
    const checkIfInstalled = () => {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
      }
      
      if (window.navigator && 'standalone' in window.navigator) {
        setIsInstalled((window.navigator as any).standalone);
      }
    };

    // Đăng ký Service Worker với registerSW từ vite-plugin-pwa
    const registerSW = async () => {
      if ('serviceWorker' in navigator) {
        try {
          // Import dynamic registerSW
          const { registerSW: register } = await import('virtual:pwa-register');
          
          const updateSW = register({
            onNeedRefresh() {
              setNeedRefresh(true);
            },
            onOfflineReady() {
              setOfflineReady(true);
            },
            onRegistered(registration) {
              setRegistration(registration || null);
            },
            onRegisterError(error) {
            },
          });
          
          // Lưu updateSW function để sử dụng sau
          (window as any).updateSW = updateSW;
          
        } catch (error) {
          // Fallback to manual registration
          try {
            const registration = await navigator.serviceWorker.register('/sw.js');
            setRegistration(registration);

            // Kiểm tra cập nhật
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed') {
                    if (navigator.serviceWorker.controller) {
                      setNeedRefresh(true);
                    } else {
                      setOfflineReady(true);
                    }
                  }
                });
              }
            });

          } catch (manualError) {
            console.log('Manual SW registration error', manualError);
          }
        }
      }
    };

    // Lắng nghe sự kiện beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as PWAInstallPrompt);
      setIsInstallable(true);
    };

    // Lắng nghe sự kiện appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
      console.log('PWA đã được cài đặt');
    };

    checkIfInstalled();
    registerSW();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!installPrompt) return;

    try {
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setInstallPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('Error installing PWA:', error);
    }
  };

  const updatePWA = () => {
    // Try to use the updateSW function from vite-plugin-pwa
    if ((window as any).updateSW) {
      (window as any).updateSW(true);
    } else if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  const closeUpdatePrompt = () => {
    setNeedRefresh(false);
  };

  const closeOfflineNotification = () => {
    setOfflineReady(false);
  };

  return {
    isInstallable,
    isInstalled,
    needRefresh,
    offlineReady,
    installPWA,
    updatePWA,
    closeUpdatePrompt,
    closeOfflineNotification,
  };
};
